import axios from "axios";
import { Link, Outlet, useLocation, useNavigation } from "react-router-dom";
import {
  AppShell,
  NavLink,
  Divider,
  Group,
  ActionIcon,
  Menu,
  Text,
  Paper,
  rem,
} from "@mantine/core";
import {
  IconSpeakerphone,
  IconDeviceDesktop,
  IconCalendar,
  IconIcons,
  IconId,
  IconCirclePlus,
  IconWorld,
  IconUser,
  IconUsers,
  IconSocial
} from "@tabler/icons-react";
import { mtsUrl } from "../lib/mts-url";
import csrfToken from "../lib/csrf-token";
import Can from "../components/Can";
import { useRequests } from "../reducers/requestsReducer";

import classes from './Root.module.css';

function onLogoutClick() {
  axios.delete(mtsUrl('users/sign_out.json'), {
    withCredentials: true,
    headers: { 'X-CSRF-Token': csrfToken() },
  }).then(() => {
    window.location.href = mtsUrl('users/sign_in');
  });
}

type MenuOption = 'all_requests' |
  'business_cards' |
  'design_projects' |
  'events' |
  'marketing_campaigns' |
  'social_media_posts' |
  'web_updates' |
  'new_request' |
  'manage_users';

export default function Root() {
  const location = useLocation();
  const { pathname, search } = location;
  const { filter } = useRequests();
  let typeFilter;
  if (filter.startsWith('type:')) typeFilter = filter.slice(5);
  let menuOption: MenuOption;

  if (pathname.indexOf('/new') >= 0) {
    menuOption = 'new_request';
  } else {
    switch(typeFilter) {
      case 'Business card':
        menuOption = 'business_cards';
        break;
      case 'Design project':
        menuOption = 'design_projects';
        break;
      case 'Event':
        menuOption = 'events';
        break;
      case 'Marketing campaign':
        menuOption = 'marketing_campaigns';
        break;
      case 'Social media post':
        menuOption = 'social_media_posts';
        break;
      case 'Web update':
        menuOption = 'web_updates';
        break;
      default:
        menuOption = 'all_requests';
    }
  }
  
  return (
    <AppShell
      bg="gray.2"
      header={{
        height: 70
      }}
      navbar={{
        width: 230,
        breakpoint: 'sm',
      }}
    >
      <AppShell.Header>
        <Group h="100%">
          <div style={{flexGrow: 1}}>
            <Text mx="md" fw={700} size={rem(20)} style={{letterSpacing: '-0.75px'}}>
              Marcomm Ticket System
            </Text>
          </div>
          <Menu>
            <Menu.Target>
              <ActionIcon variant="transparent" mx="md">
                <IconUser />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item>
                <Link to="/settings">Settings</Link>
              </Menu.Item>
              <Menu.Item onClick={onLogoutClick}>Logout</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar bg="gray.2" withBorder={false}>
        <Paper bg="gray.2" mt="lg" mr="lg">
          <NavLink
            className={classes.primaryNavLink}
            to="/requests"
            label="All Requests"
            fw={500}
            component={Link}
            leftSection={<IconWorld stroke={1.25} />}
            active={menuOption === 'all_requests'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:Business card"
            label="Business cards"
            fw={500}
            component={Link}
            leftSection={<IconId stroke={1.25} />}
            active={menuOption === 'business_cards'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:Design project"
            label="Design projects"
            fw={500}
            component={Link}
            leftSection={<IconIcons stroke={1.25} />}
            active={menuOption === 'design_projects'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:Event"
            label="Events"
            fw={500}
            component={Link}
            leftSection={<IconCalendar stroke={1.25} />}
            active={menuOption === 'events'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:Marketing campaign"
            label="Marketing campaigns"
            fw={500}
            component={Link}
            leftSection={<IconSpeakerphone stroke={1.25} />}
            active={menuOption === 'marketing_campaigns'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:Social media post"
            label="Social media posts"
            fw={500}
            component={Link}
            leftSection={<IconSocial stroke={1.25} />}
            active={menuOption === 'social_media_posts'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:Web update"
            label="Web updates"
            fw={500}
            component={Link}
            leftSection={<IconDeviceDesktop stroke={1.25} />}
            active={menuOption === 'web_updates'}
          />
          <Divider />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests/new/type"
            component={Link}
            label="New request"
            fw={500}
            leftSection={<IconCirclePlus stroke={1.25} />}
            active={menuOption === 'new_request'}
          />
          <Can do="manage" on="User">
            <Divider />
            <NavLink
              className={classes.primaryNavLink}
              href="#required-for-focus"
              label="Users"
              fw={500}
              leftSection={<IconUsers stroke={1.25} />}
            />
          </Can>
        </Paper>
      </AppShell.Navbar>
      <AppShell.Main
        maw="var(--mantine-breakpoint-xl)"
        pb="lg"
      >
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
